import { format } from "date-fns";
import { Share2, Sparkles, Sprout, Trophy, Compass, Star, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface EventAchievementCardProps {
  achievementType: 'first_event' | 'attended_5' | 'attended_10' | 'attended_25' | 'attended_50' | 'attended_75' | 'attended_100';
  username: string;
  earnedDate: string;
  compact?: boolean;
}

const ACHIEVEMENT_CONFIG = {
  first_event: {
    title: "New to the Herd",
    gradient: "from-emerald-500 to-green-600",
    bgGradient: "from-emerald-500/20 to-green-600/20",
    quote: "Welcome to the herd, cowgirl!",
    events: 1,
  },
  attended_5: {
    title: "Getting Started",
    gradient: "from-blue-500 to-teal-600",
    bgGradient: "from-blue-500/20 to-teal-600/20",
    quote: "You're finding your stride!",
    events: 5,
  },
  attended_10: {
    title: "Flow Finder",
    gradient: "from-cyan-500 to-blue-600",
    bgGradient: "from-cyan-500/20 to-blue-600/20",
    quote: "You've found your flow!",
    events: 10,
  },
  attended_25: {
    title: "Balanced Cowgirl",
    gradient: "from-pink-500 to-rose-600",
    bgGradient: "from-pink-500/20 to-rose-600/20",
    quote: "Balance is your superpower!",
    events: 25,
  },
  attended_50: {
    title: "Wellness Wrangler",
    gradient: "from-purple-500 to-indigo-600",
    bgGradient: "from-purple-500/20 to-indigo-600/20",
    quote: "Wrangling wellness like a pro!",
    events: 50,
  },
  attended_75: {
    title: "Iconic Cowgirl",
    gradient: "from-amber-500 to-yellow-600",
    bgGradient: "from-amber-500/20 to-yellow-600/20",
    quote: "You're an icon in the making!",
    events: 75,
  },
  attended_100: {
    title: "Legendary Yogi Cowgirl",
    gradient: "from-pink-500 via-purple-500 to-yellow-500",
    bgGradient: "from-pink-500/20 via-purple-500/20 to-yellow-500/20",
    quote: "Hall of Fame status!",
    events: 100,
  },
};

export const EventAchievementCard = ({
  achievementType,
  username,
  earnedDate,
  compact = false,
}: EventAchievementCardProps) => {
  const config = ACHIEVEMENT_CONFIG[achievementType];

  const handleShare = async () => {
    const shareText = `🎉 I just earned the "${config.title}" badge on Wellness Cowgirl for attending ${config.events} event${config.events > 1 ? 's' : ''}! ${config.quote}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${config.title} Achievement`,
          text: shareText,
        });
        toast({
          title: "Shared!",
          description: "Your achievement has been shared.",
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast({
          title: "Copied!",
          description: "Achievement text copied to clipboard.",
        });
      } catch (error) {
        console.error('Error copying:', error);
        toast({
          title: "Error",
          description: "Could not copy to clipboard.",
          variant: "destructive",
        });
      }
    }
  };

  if (compact) {
    return (
      <div className={`bg-gradient-to-br ${config.bgGradient} rounded-xl p-4 border border-border/50`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <span className="text-white text-lg font-bold">{config.events}</span>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-foreground text-sm">{config.title}</h4>
            <p className="text-xs text-muted-foreground">{config.events} event{config.events > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br ${config.bgGradient} rounded-2xl p-6 border border-border/50 space-y-4`}>
      {/* Title Badge */}
      <div className="flex flex-col items-center text-center space-y-4">
        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center card-shadow`}>
          <span className="text-white text-2xl font-bold">{config.events}</span>
        </div>
        
        <div>
          <h3 className="font-display text-2xl font-semibold text-foreground mb-1">{config.title}</h3>
          <p className="text-sm text-muted-foreground">
            {config.events} event{config.events > 1 ? 's' : ''} attended
          </p>
        </div>
      </div>

      {/* Quote */}
      <div className="text-center">
        <p className="text-foreground/80 italic">"{config.quote}"</p>
      </div>

      {/* User Info */}
      <div className="flex items-center justify-between pt-4 border-t border-border/30">
        <div>
          <p className="text-sm font-semibold text-foreground">{username}</p>
          <p className="text-xs text-muted-foreground">
            Earned {format(new Date(earnedDate), 'MMM d, yyyy')}
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          className="gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      </div>
    </div>
  );
};